import { useState, useEffect } from 'react'
import type { DateTimeValueMapping } from './CSVDateTimeSelector.js'
import { isValidDate } from './CSVDateTimeSelector.js'

type MapDateTimeValuesModalProps = {
  invalidValues: Set<string>
  mapping: Record<string, DateTimeValueMapping>
  handleMappingChange: (
    changes: Record<string, DateTimeValueMapping>
  ) => void
  close: () => void
}

export function MapDateTimeValuesModal({
  invalidValues,
  mapping,
  handleMappingChange,
  close
}: MapDateTimeValuesModalProps) {
  const [bufferedMapping, setBufferedMapping] = useState<typeof mapping>({})
  const [customValues, setCustomValues] = useState<Record<string, string>>({})
  const [invalidCustomInputs, setInvalidCustomInputs] = useState<Set<string>>(new Set())

  const validateCustomValue = (key: string, value: string) => {
    const isValid = isValidDate(value, false, false)
    setInvalidCustomInputs((prev) => {
      const newSet = new Set(prev)
      if (isValid) newSet.delete(key)
      else newSet.add(key)
      return newSet
    })
  }

  // Copy initial mapping into local state
  useEffect(() => {
    setBufferedMapping(mapping)

    // Initialize custom values from existing mappings
    const initialCustomValues: Record<string, string> = {}
    Object.entries(mapping).forEach(([key, value]) => {
      if (value.type === 'MAP_TO') {
        initialCustomValues[key] = value.value
      }
    })
    setCustomValues(initialCustomValues)
  }, [mapping])

  const handleBufferedChange = (
    value: string,
    mappingType: DateTimeValueMapping
  ) => {
    setBufferedMapping((prev) => ({ ...prev, [value]: mappingType }))
  }

  const handleCustomValueChange = (originalValue: string, newValue: string) => {
    setCustomValues((prev) => ({ ...prev, [originalValue]: newValue }))
    validateCustomValue(originalValue, newValue)
    handleBufferedChange(originalValue, { type: 'MAP_TO', value: newValue })
  }

  const applyChanges = () => {
    handleMappingChange(bufferedMapping)
    close()
  }

  const singleEntry = (
    value: string
  ) => {
    const isDiscard = bufferedMapping[value]?.type === 'DISCARD'
    const isMapped = bufferedMapping[value]?.type === 'MAP_TO'

    return (
      <div key={value} className="mb-2 p-1 border border-gray-200 rounded-lg">
        <div className="font-semibold mb-0.5 text-sm">&quot;{value}&quot;</div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={isDiscard}
                onChange={() => handleBufferedChange(value, { type: 'DISCARD' })}
              />
              Discard this value
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={isMapped}
                onChange={() => handleBufferedChange(value, { type: 'MAP_TO', value: customValues[value] || value })}
              />
              Edit as custom value
            </label>
          </div>

          {isMapped && (
            <div className="ml-6">
              <input
                type="text"
                className={`input input-bordered input-sm w-full ${
                  invalidCustomInputs.has(value) ? 'border-red-500' : ''
                }`}
                value={customValues[value] || ''}
                onChange={(e) => handleCustomValueChange(value, e.target.value)}
                placeholder="Enter valid date/time (e.g., 2023-01-01T12:00:00Z)"
              />
              {invalidCustomInputs.has(value) && (
                <div className="text-xs text-red-500 mt-1">
                  Invalid date format. Please enter a valid ISO or recognizable date.
                </div>
              )}
              <div className="text-xs text-gray-500 mt-1">
                Enter a valid date/time string in ISO format or any other supported format
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div>
      <h3 className="font-bold text-lg mb-4">Map Date/Time Values</h3>
      <p className="mb-4">
        This dialog shows values that couldn&apos;t be parsed as valid dates/times. 
        You can either discard these values or edit them to be valid date/time strings.
      </p>

      <div className="overflow-y-auto max-h-96">
        {invalidValues.size > 0 && (
          <div>
            <h4 className="font-semibold text-md mb-2">Invalid Date/Time Values</h4>
            {Array.from(invalidValues).map((value) =>
              singleEntry(value)
            )}
          </div>
        )}

        {Object.entries(bufferedMapping)
          .filter(([key]) => !invalidValues.has(key))
          .length > 0 && (
          <div
            className={
              invalidValues.size > 0
                ? 'mt-6 pt-6 border-t border-gray-300'
                : ''
            }
          >
            <h4 className="font-semibold text-md mb-2">Already Mapped Values</h4>
            {Object.entries(bufferedMapping)
              .filter(([key]) => !invalidValues.has(key))
              .map(([value]) => singleEntry(value))}
          </div>
        )}
      </div>

      <div className="modal-action flex justify-end gap-2 mt-4">
        <button className="btn btn-outline" onClick={close}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={applyChanges}
          disabled={invalidCustomInputs.size > 0}
        >
          Apply
        </button>
      </div>
    </div>
  )
}
